<template>
	<div class="spotlight-search">
		<div class="search-input-container">
			<IconSvg
				name="magnifying-glass"
				class="search-icon"
			/>
			<input
				ref="searchInput"
				v-model="searchQuery"
				class="search-input"
				placeholder="Search creators, projects, etc..."
			/>
		</div>

		<div
			v-if="hasResults"
			:class="isShowingDefaults ? 'default-results' : 'results-container'"
		>
			<SpotlightSearchGroup
				v-for="(group, groupType) in displayResults"
				:key="groupType"
				:group-type="groupType"
				:results="group"
				:is-showing-defaults="isShowingDefaults"
				@select="selectResult"
			/>
		</div>

		<div
			v-else-if="!isShowingDefaults"
			class="empty-state"
		>
			<div class="empty-title">No results found</div>
			<div class="empty-description">Try searching by name, email, username, or keywords</div>
		</div>
	</div>
</template>

<script lang="ts">
import Vue from 'vue'
import IconSvg from '@cv/components/atoms/IconSvg.vue'
import SpotlightSearchGroup from '@cv/components/molecules/SpotlightSearchGroup.vue'
import {
	ScoredSpotlightResult,
	GroupedResults,
	SearchResultGroupName
} from '@cv/store/spotlight-search'

export default Vue.extend({
	name: 'SpotlightSearch',
	components: {
		IconSvg,
		SpotlightSearchGroup
	},
	props: {
		isShowing: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			searchQuery: '',
			searchTimeout: null as number | null,
			// Track active in state to avoid DOM scans for ".is-active"
			activeIndex: -1 as number,
			activeId: null as string | null
		}
	},

	computed: {
		isShowingDefaults(): boolean {
			return this.searchQuery.trim().length < 2
		},

		displayResults(): GroupedResults {
			if (this.isShowingDefaults) {
				return this.$store.getters['spotlightSearch/defaultResults']()
			}
			const searchResults = this.$store.getters['spotlightSearch/search'](
				this.searchQuery.trim()
			)
			return this.$store.getters['spotlightSearch/groupResults'](searchResults)
		},

		hasResults(): boolean {
			return Object.keys(this.displayResults).length > 0
		}
	},

	watch: {
		isShowing(show: boolean) {
			// Attach / detach the global listener as visibility changes
			if (show) {
				window.addEventListener('keydown', this.handleGlobalKeydown, { passive: false })
				// small UX nicety: refocus when opening
				this.$nextTick(() => this.focusInput())
			} else {
				window.removeEventListener('keydown', this.handleGlobalKeydown as any)
				this.clearActive()
			}
		},

		searchQuery() {
			this.debouncedSearch()
			this.clearActive()
		}
	},

	mounted() {
		this.$store.dispatch('spotlightSearch/ensureIndexBuilt')
		this.$nextTick(() => this.focusInput())

		// Only listen globally when visible
		if (this.isShowing) {
			window.addEventListener('keydown', this.handleGlobalKeydown, { passive: false })
		}
	},

	beforeDestroy() {
		window.removeEventListener('keydown', this.handleGlobalKeydown as any)
	},

	methods: {
		focus() {
			this.focusInput()
		},

		focusInput() {
			const el = this.$refs.searchInput as HTMLInputElement | undefined
			if (el) el.focus()
		},

		debouncedSearch() {
			if (this.searchTimeout != null) window.clearTimeout(this.searchTimeout)
			this.searchTimeout = window.setTimeout(() => {
				// placeholder for remote queries if needed
			}, 200)
		},

		// ===== DOM helpers (called at most once per keydown) =====
		getAllResultEls(): HTMLElement[] {
			const root = this.$el as HTMLElement
			return Array.from(root.querySelectorAll<HTMLElement>('.spotlight-result'))
		},

		// ===== Active-item state helpers =====
		clearActive() {
			const root = this.$el as HTMLElement
			const prev =
				this.activeIndex >= 0
					? this.getAllResultEls()[this.activeIndex]
					: root.querySelector<HTMLElement>('.spotlight-result.is-active')

			if (prev) prev.classList.remove('is-active')
			this.activeIndex = -1
			this.activeId = null
		},

		ensureActiveIsValid(items: HTMLElement[]) {
			// If list changed (show more/less), try to keep selection by id
			if (!items.length) {
				this.activeIndex = -1
				this.activeId = null
				return
			}

			if (this.activeId) {
				const idx = items.findIndex(el => String(el.dataset.id) === this.activeId)
				if (idx !== -1) {
					this.activeIndex = idx
					return
				}
			}

			// If no valid active, don't auto-select here; movement will select.
			if (this.activeIndex >= items.length) {
				this.activeIndex = -1
				this.activeId = null
			}
		},

		setActiveByIndex(index: number, items: HTMLElement[]) {
			if (!items.length) return

			const safe = ((index % items.length) + items.length) % items.length

			// Remove previous class only once, without re-querying
			if (this.activeIndex !== -1 && items[this.activeIndex]) {
				items[this.activeIndex].classList.remove('is-active')
			}

			const el = items[safe]
			el.classList.add('is-active')

			this.activeIndex = safe
			this.activeId = String(el.dataset.id || '')

			this.scrollIntoViewIfNeeded(el)
		},

		moveActive(delta: number, items: HTMLElement[]) {
			if (!items.length) return

			// Align state with current DOM once per keypress
			this.ensureActiveIsValid(items)

			if (this.activeIndex === -1) {
				// If nothing is active yet, pick first/last depending on direction
				this.setActiveByIndex(delta > 0 ? 0 : items.length - 1, items)
			} else {
				this.setActiveByIndex(this.activeIndex + delta, items)
			}
		},

		scrollIntoViewIfNeeded(el: HTMLElement) {
			const container = el.closest(
				'.results-container, .default-results'
			) as HTMLElement | null
			if (!container) return
			const cRect = container.getBoundingClientRect()
			const eRect = el.getBoundingClientRect()
			if (eRect.top < cRect.top || eRect.bottom > cRect.bottom) {
				el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
			}
		},

		// Activation uses current state + single DOM pass (items already computed)
		activateSelectionFromState(items: HTMLElement[]) {
			if (this.activeIndex < 0 || this.activeIndex >= items.length) return
			const el = items[this.activeIndex]
			const group = el.dataset.group as SearchResultGroupName | undefined
			const id = el.dataset.id
			if (!group || !id) return

			const groupArr: ScoredSpotlightResult[] = (this.displayResults as any)[group] || []
			const result = groupArr.find(r => String(r.id) === String(id))
			if (!result) return

			this.searchQuery = ''

			if (result.onSelect) {
				const actionResult = result.onSelect() as any
				if (actionResult?.type === 'account-switch') {
					this.$store.commit('account/SET_SELECTED_ACCOUNT', actionResult.accountId)
					this.$router.push({ name: 'home' })
				}
			} else if (result.route) {
				this.$router.push(result.route)
			}

			this.$emit('close')
		},

		// For mouse selection we still receive the resolved result directly
		selectResult(result: ScoredSpotlightResult) {
			this.searchQuery = ''
			if (result.onSelect) {
				const actionResult = result.onSelect() as any
				if (actionResult?.type === 'account-switch') {
					this.$store.commit('account/SET_SELECTED_ACCOUNT', actionResult.accountId)
					this.$router.push({ name: 'home' })
				}
			} else if (result.route) {
				this.$router.push(result.route)
			}
			this.$emit('close')
		},

		// Utility: is the event target a text-entry widget?
		isTextEntryTarget(el: EventTarget | null) {
			const node = el as HTMLElement | null
			if (!node) return false
			const tag = node.tagName?.toLowerCase()
			if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
			if (node.isContentEditable) return true
			return false
		},

		isInteractiveClickTarget(el: EventTarget | null) {
			const node = el as HTMLElement | null
			if (!node) return false
			const tag = node.tagName?.toLowerCase()
			if (tag === 'button' || tag === 'a') return true
			const role = node.getAttribute('role')
			return role === 'button' || role === 'link' || role === 'menuitem'
		},

		handleGlobalKeydown(event: KeyboardEvent) {
			// Only handle when visible
			if (!this.isShowing) return

			// Allow typing in normal inputs/selects/contenteditable, except our search input
			const searchEl = this.$refs.searchInput as HTMLInputElement | undefined
			const targetIsSearch = searchEl && event.target === searchEl
			const targetIsTextEntry = this.isTextEntryTarget(event.target)

			// If user is typing in a text field that's NOT the search box, don't hijack arrows.
			if (targetIsTextEntry && !targetIsSearch) return

			// Fresh list ONCE per keypress (still respects show more/less)
			const items = this.getAllResultEls()
			const anyItems = items.length > 0

			switch (event.key) {
				case 'ArrowDown':
					if (!anyItems) return
					event.preventDefault()
					this.moveActive(1, items)
					break
				case 'ArrowUp':
					if (!anyItems) return
					event.preventDefault()
					this.moveActive(-1, items)
					break
				case 'Enter':
					// If focused element is inherently interactive (button/link), let it do its default.
					if (this.isInteractiveClickTarget(event.target)) return
					if (!anyItems) return
					event.preventDefault()
					this.activateSelectionFromState(items)
					break
				case 'Escape':
					// Let inputs handle Escape to clear, but still close modal if not in a text field
					if (!targetIsTextEntry || targetIsSearch) {
						this.$emit('close')
					}
					break
			}
		}
	}
})
</script>

<style lang="less" scoped>
@import '../../less/variables';

.spotlight-search {
	width: 100%;
	max-width: 600px;
	margin: 0 auto;
	padding-top: 1.25rem;
}

.search-input-container {
	position: relative;
	margin-bottom: 1rem;
}

.search-icon {
	position: absolute;
	left: 1rem;
	top: 50%;
	transform: translateY(-50%);
	color: @grayMedium;
	width: 16px;
	height: 16px;
	pointer-events: none;
}

.search-input {
	width: 100%;
	padding: 0.875rem 1rem 0.875rem 2.5rem;
	border-radius: 50px;
	font-size: 1rem;
	font-family: inherit;
	background: @grayLight;
	border: none;
	outline: none;
	transition:
		border-color 0.15s,
		box-shadow 0.15s;
	box-sizing: border-box;
	&:focus {
		/* border-color removed since border is none; focus ring does the work */
		box-shadow: 0 0 0 3px fade(@purpleVibrant, 20%);
	}
	&::placeholder {
		color: @grayMedium;
	}
}

.results-container,
.default-results {
	max-height: 60vh;
	overflow-y: auto;
	// hide the scrollbar
	&::-webkit-scrollbar {
		display: none;
	}
}

.empty-state {
	text-align: center;
	padding: 3rem 2rem;
	color: @grayMedium;
}

.empty-title {
	font-size: 1.125rem;
	font-weight: 500;
	margin-bottom: 0.5rem;
	color: @grayDark;
}

.empty-description {
	font-size: 0.875rem;
	line-height: 1.4;
}

/* Custom scrollbar */
.results-container::-webkit-scrollbar,
.default-results::-webkit-scrollbar {
	width: 6px;
}

.results-container::-webkit-scrollbar-track,
.default-results::-webkit-scrollbar-track {
	background: @grayExtraLight;
	border-radius: 3px;
}

.results-container::-webkit-scrollbar-thumb,
.default-results::-webkit-scrollbar-thumb {
	background: @grayLight;
	border-radius: 3px;
}

.results-container::-webkit-scrollbar-thumb:hover,
.default-results::-webkit-scrollbar-thumb:hover {
	background: @grayMedium;
}
</style>
